import { STORAGE_KEYS } from 'constants/storageKeys';

type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

function safeGetItem(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    console.warn(`[Persistence] Failed to set ${key}`, error);
  }
}

function safeRemoveItem(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.warn(`[Persistence] Failed to remove ${key}`, error);
  }
}

/**
 * Typed persistence layer for localStorage access.
 * Use these helpers instead of direct localStorage calls for consistency and error handling.
 */
export const persistence = {
  getItem(key: StorageKey | string): string | null {
    return safeGetItem(key);
  },

  setItem(key: StorageKey | string, value: string): void {
    safeSetItem(key, value);
  },

  removeItem(key: StorageKey | string): void {
    safeRemoveItem(key);
  },

  getJSON<T>(key: StorageKey | string, defaultValue: T): T {
    const raw = safeGetItem(key);
    if (raw == null) return defaultValue;
    try {
      return JSON.parse(raw) as T;
    } catch (error) {
      console.warn(`[Persistence] Corrupted JSON for key ${key}, removing`, error);
      safeRemoveItem(key);
      return defaultValue;
    }
  },

  setJSON(key: StorageKey | string, value: unknown): void {
    safeSetItem(key, JSON.stringify(value));
  },
};
